import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Root, SectionMenu, ContainerMenu, Options,
  SectionMasters, Filters, SearchContainer, SearchText, ArrowImg,
  PaginationWrapper,
   Placeholder,
   LoaderWrapper
} from './styles';
import axios from './../../utils/axios';
import MastersCard from './master-card/master-card';
import arrow from './master-card/assets/down-arrow.svg';
import MasterSearch from './master-modal-search/master-search';
import debounce from 'lodash.debounce';
import { usePagination } from '../../hooks/usePagination';
import countriesLanguages from '@hotosm/iso-countries-languages';
import { CustomSelect } from '../../ui-kit/custom-select/custom-select';
import LoaderCircle from '../../ui-kit/loader-circle/loader-circle';


const PAGE_SIZE = 20;

const MastersPage = () => {
  const { t, i18n } = useTranslation();
  const [masters, setMasters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpenSearch, setIsOpenSearch] = useState(false);
  const [destination, setDestination] = useState('')
  const [loading, setLoading] = useState(false);
  const countriesList = countriesLanguages.getCountries(i18n.language);
  const countriesListSorter = Object.entries(countriesList).sort(([, v1], [, v2]) => v1 < v2 ? -1 : v1 > v2 ? 1 : 0).filter(([k]) => k !== 'LS')
  const totalPages = Math.ceil(masters.length / PAGE_SIZE);
  const sortedMasters = [...masters].sort((a, b) =>
    a.username.trimStart().localeCompare(b.username.trimStart(), 'ru', { sensitivity: 'base' })
  );
  const searchRef = useRef(null);
  const masterSearchRef = useRef(null);

  const { paginationComponent, currentPage } = usePagination({
    totalPages,
  });

  const paginatedMasters = sortedMasters.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  const getMasters = useCallback(async (search = '') => {
    setLoading(true)
    const { data } = await axios.get(`/masters`, { params: { q: search, country: destination} });
    setMasters(data);
    setLoading(false)
  }, [destination]);

  const debouncedFetch = useMemo(
    () => debounce(() => getMasters(searchTerm), 500),
    [getMasters, searchTerm]
  );

  useEffect(() => {
    debouncedFetch();
    return () => debouncedFetch.cancel();
  }, [debouncedFetch]);

  useEffect(() => {
    getMasters();
  }, [getMasters, destination]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target) &&
        masterSearchRef.current &&
        !masterSearchRef.current.contains(event.target)
      ) {
        setIsOpenSearch(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  return (
    <Root>
              <SectionMenu>
                <ContainerMenu>
                  <Options>{t('masters')}</Options>
                  <Filters>
                    <CustomSelect
                      placeholder={
                        <Placeholder>
                            <SearchText style={{ color: '#000000'}}>{t('allCountry')}</SearchText>
                            <ArrowImg alt="arrow" src={arrow} />
                        </Placeholder>
                      } 
                      width={'180px'} 
                      mobileWidth={'140px'} 
                      value={destination} 
                      showShevron={false} 
                      withBorder={false}
                      onClick={() => setIsOpenSearch(false)}
                    >
                        <li value={''} key={'all-country'} onClick={() => setDestination('')}>{t('allCountry')}</li>
                        {countriesListSorter.map(([k, v]) => (
                            <li value={k} key={k} onClick={() => setDestination(k)}>{k === 'BY' && i18n.language === 'ru' ? 'Беларусь' : v}</li>
                        ))}
                    </CustomSelect>
                    <SearchContainer ref={searchRef} onClick={() => setIsOpenSearch(!isOpenSearch)}>
                      <SearchText>{t('search')}</SearchText>
                      <ArrowImg alt="arrow" src={arrow} />
                    </SearchContainer>
                    {isOpenSearch && <MasterSearch onChange={setSearchTerm} ref={masterSearchRef}/>}
                  </Filters>
                </ContainerMenu>
              </SectionMenu>

               {loading ? 
                  <LoaderWrapper>
                      <LoaderCircle/>
                  </LoaderWrapper>
                    :
                    <>
                      <SectionMasters>
                        {paginatedMasters.map((master) => (
                          <MastersCard
                            key={master._id}
                            avatar={master.avatarUrl}
                            shopName={master.shopname}
                            name={`${master.username} ${master.surname}`}
                            country={master.country}
                            link={master.shortlink || master._id}
                            subs={master.subsCount}
                            workItemFirst={master.cards[0]?.imgUrl[0]}
                            workItemSecond={master.cards[1]?.imgUrl[0]}
                            numberWorks={master.cards.length}
                          />
                        ))}
                      </SectionMasters>
                      <PaginationWrapper>
                        {paginationComponent}
                      </PaginationWrapper>
                    </>
               }
    </Root>
  );
};

export default MastersPage;